import { useEffect, useState } from 'react';
import { isSupabaseConfigured, supabase, type Session } from './supabase';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'signed_in'; session: Session; isAdmin: boolean | null };

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setState({ status: 'signed_out' });
      return;
    }

    let cancelled = false;

    async function probeAdmin(session: Session) {
      const { data, error } = await supabase
        .from('admin_users')
        .select('user_id, role')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.warn('[admin] is_admin probe failed:', error.message);
        setState({ status: 'signed_in', session, isAdmin: false });
        return;
      }

      setState({ status: 'signed_in', session, isAdmin: !!data });
    }

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        if (data.session) {
          setState({ status: 'signed_in', session: data.session, isAdmin: null });
          void probeAdmin(data.session);
        } else {
          setState({ status: 'signed_out' });
        }
      })
      .catch((err: unknown) => {
        console.warn('[admin] getSession failed:', err);
        if (!cancelled) setState({ status: 'signed_out' });
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) {
        setState({ status: 'signed_in', session, isAdmin: null });
        void probeAdmin(session);
      } else {
        setState({ status: 'signed_out' });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function sendMagicLink(email: string, redirectTo: string) {
  return await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectTo,
      shouldCreateUser: true,
    },
  });
}
